import { FC, ReactNode, createContext, useContext, useMemo } from "react";

import OverlayLoading from "src/components/DataStates/OverlayLoading";
import PageError from "src/components/Errors/PageError";
import { useGetCurrentUserQuery } from "src/graphql/anti-fraud/queries/users/GetCurrentUserQuery";
import { User } from "src/types/user";

export type CurrentUserContextValue = {
  currentUser: User | null;
};

export const CurrentUserContext = createContext<CurrentUserContextValue>({
  currentUser: null,
});

export const CurrentUserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data: user, loading, error } = useGetCurrentUserQuery();

  const contextValue = useMemo(
    () => ({
      currentUser: user,
    }),
    [user]
  );

  return error ? (
    <PageError title={error.title} description={error.message} />
  ) : loading ? (
    <OverlayLoading />
  ) : (
    <CurrentUserContext.Provider value={contextValue}>{children}</CurrentUserContext.Provider>
  );
};

export const useCurrentUser = () => {
  const { currentUser } = useContext(CurrentUserContext);

  if (!currentUser) {
    throw new Error(
      "Either `useCurrentUser` was called outside of a `CurrentUserProvider` or the provider was not properly initialized."
    );
  }

  return { currentUser };
};
